import { Component, EventEmitter, inject, Input, OnInit, Output, signal, computed, effect } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SessionRetakePhotoComponent } from '@components/session-retake-photo/session-retake-photo.component';
import { Photo } from '@models/photo';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { OverlayService } from '@services/overlay';
import { PhotoTaken } from 'src/app/interfaces/photo-taken.interface';
import { PhotoType } from 'src/app/types/photo.type';
import { CommonModule } from '@angular/common';
import { IconDirective } from '../../directives/icon.directive';
import { RetakeRequest, Session } from 'src/app/queries/generated';

@Component({
  selector: 'lv-session-retake-request-form-item',
  templateUrl: './session-retake-request-form-item.component.html',
  styleUrls: ['./session-retake-request-form-item.component.scss'],
  imports: [CommonModule, TranslateModule, IconDirective]
})
export class SessionRetakeRequestFormItemComponent implements OnInit {
  private overlayService = inject(OverlayService);
  private sanitizer = inject(DomSanitizer);
  private translateService = inject(TranslateService);

  @Input() retakeRequest!: RetakeRequest;
  @Input() photoType!: PhotoType;
  @Input() session!: Session;
  @Output() photoTaken = new EventEmitter<PhotoTaken>();

  photo = signal<Photo>(null);
  photoURL = computed(() => {
    const photo = this.photo();
    return photo ? this.sanitizer.bypassSecurityTrustUrl(photo.image) : null;
  });

  /// Removes the red border from the component when the photo is retaken.
  retaken = false;

  // Effect to watch for photo changes and emit photoTaken events
  private photoEffect = effect(() => {
    const photo = this.photo();
    if (photo) {
      this.retaken = true;
      this.photoTaken.emit({ photoType: this.photoType, photo });
    }
  });

  get valid(): boolean {
    const key = `${this.photoType}Photo`;

    return !this.retakeRequest[key];
  }

  get mainClass(): string {
    if (this.retaken) {
      return 'retaken';
    }

    return this.valid ? 'valid' : 'invalid';
  }

  get headLabel(): string {
    return `session-retake-request-form-item.component.${this.photoType}-photo`;
  }

  get reasonLabel(): string {
    return `session-retake-request-form-item.component.${this.photoType}-photo-reason`;
  }

  get reason(): string {
    if (this.valid) {
      return this.translateService.instant('session-retake-request-form-item.component.all-good');
    }

    const key = `${this.photoType}PhotoReason`;

    return this.retakeRequest[key] || '';
  }

  get currentPhotoUrl(): string {
    const key = `${this.photoType}Photo`;

    return this.session.activeIdentification[key].url;
  }

  ngOnInit() {
    if (this.retakeRequest) {
      if (this.valid) {
        this.photoTaken.emit({
          photoType: this.photoType,
          photo: this.photo()
        });
      }
    }
  }

  onButtonClick() {
    this.overlayService.open(SessionRetakePhotoComponent, {
      photoType: this.photoType,
      session: this.session,
      photo: this.photo
    });
  }
}
